import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { TFunction } from 'i18next';
import type { StructuredData } from '../api/types';
import type { ContractorSettings } from '../lib/contractorSettings';

export interface PdfLabels {
  // Table headers
  description: string;
  quantity: string;
  unitPrice: string;
  itemTotal: string;
  vatColumn: string; // e.g. "VAT (22%)" or "KM (22%)"
  // Footer
  subtotal: string;
  grandTotal: string;
  vatLine: string; // e.g. "VAT (22%)" or "KM (22%)"
  total: string;
  // Contractor
  regLabel: string;
  vatLabel: string;
  ibanLabel: string;
  // Page footer
  generatedBy: string;
  // Notes
  notesHeading: string;
  // Quote metadata
  quoteFor: string;
  dateLabel: string;
  refLabel: string;
  paymentTerms: string; // Already formatted with days
  validUntilLabel: string;
  // Invoice metadata
  billTo: string;
  invoiceTitle: string;
  invoiceNumberLabel: string;
  invoiceDateLabel: string;
  dueDateLabel: string;
  quoteRefLabel: string;
  paymentInfoHeading: string;
}

const DEFAULT_LABELS: PdfLabels = {
  description: 'Description',
  quantity: 'Qty',
  unitPrice: 'Unit Price',
  itemTotal: 'Total',
  vatColumn: 'VAT',
  subtotal: 'Subtotal',
  grandTotal: 'Grand Total',
  vatLine: 'VAT',
  total: 'Total',
  regLabel: 'Reg',
  vatLabel: 'VAT',
  ibanLabel: 'IBAN',
  generatedBy: 'Generated by VoiceQuote',
  notesHeading: 'Notes',
  quoteFor: 'Quote for',
  dateLabel: 'Date',
  refLabel: 'Ref',
  paymentTerms: '',
  validUntilLabel: 'Valid until',
  billTo: 'Bill to',
  invoiceTitle: 'INVOICE',
  invoiceNumberLabel: 'Invoice No.',
  invoiceDateLabel: 'Invoice Date',
  dueDateLabel: 'Due Date',
  quoteRefLabel: 'Quote Ref',
  paymentInfoHeading: 'Payment Information',
};

export function resolveLabels(partial?: Partial<PdfLabels>): PdfLabels {
  return { ...DEFAULT_LABELS, ...partial };
}

/**
 * Build the common PDF labels shared across quote, invoice, and share buttons.
 * Eliminates duplication of the label-building block in each component.
 */
export function buildCommonPdfLabels(t: TFunction, vatRate: number | null): Partial<PdfLabels> {
  return {
    generatedBy: t('pdf.generatedBy'),
    description: t('pdf.description'),
    quantity: t('pdf.quantity'),
    unitPrice: t('pdf.unitPrice'),
    itemTotal: t('pdf.itemTotal'),
    vatColumn: vatRate === null ? '' : t('pdf.vatColumn', { rate: vatRate }),
    subtotal: t('pdf.subtotal'),
    grandTotal: t('pdf.grandTotal'),
    vatLine: vatRate === null ? '' : t('pdf.vatLine', { rate: vatRate }),
    total: t('pdf.total'),
    regLabel: t('pdf.regLabel'),
    vatLabel: t('pdf.vatLabel'),
    ibanLabel: t('pdf.ibanLabel'),
  };
}

const LOGO_MAX_WIDTH_MM = 40;
const LOGO_DEFAULT_HEIGHT_MM = 20;
const LOGO_GAP_MM = 4;
const LOGO_X = 14;
const LOGO_Y = 14;

export function renderLogo(
  doc: jsPDF,
  logo: string | null | undefined,
  logoHeight = LOGO_DEFAULT_HEIGHT_MM,
): number {
  if (!logo) return 0;

  const format = logo.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
  doc.addImage(logo, format, LOGO_X, LOGO_Y, LOGO_MAX_WIDTH_MM, logoHeight);

  return logoHeight + LOGO_GAP_MM;
}

export function getTableFinalY(doc: jsPDF): number {
  // jspdf-autotable adds lastAutoTable to the doc instance at runtime
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (doc as any).lastAutoTable.finalY;
}

export function buildFooterRows(
  grandTotal: number,
  vatRate: number | null,
  markupPercent: number | null,
  labels?: Partial<PdfLabels>,
): string[][] {
  const l = resolveLabels(labels);
  const markupAmount = markupPercent ? grandTotal * (markupPercent / 100) : 0;
  const taxableBase = grandTotal + markupAmount;

  if (vatRate === null) {
    return [['', '', l.grandTotal, `€${taxableBase.toFixed(2)}`]];
  }

  const vatAmount = taxableBase * (vatRate / 100);
  return [
    ['', '', l.subtotal, `€${taxableBase.toFixed(2)}`],
    ['', '', l.vatLine, `€${vatAmount.toFixed(2)}`],
    ['', '', l.total, `€${(taxableBase + vatAmount).toFixed(2)}`],
  ];
}

export function renderContractorDetails(
  doc: jsPDF,
  contractor: ContractorSettings,
  pageWidth: number,
  includeIban: boolean,
  labels?: Partial<PdfLabels>,
  yOffset = 0,
): void {
  const l = resolveLabels(labels);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  let rightY = 14 + yOffset;
  const rightX = pageWidth - 14;

  if (contractor.address) {
    doc.text(contractor.address, rightX, rightY, { align: 'right' });
    rightY += 5;
  }
  if (contractor.regCode) {
    doc.text(`${l.regLabel}: ${contractor.regCode}`, rightX, rightY, { align: 'right' });
    rightY += 5;
  }
  if (contractor.vatNumber) {
    doc.text(`${l.vatLabel}: ${contractor.vatNumber}`, rightX, rightY, { align: 'right' });
    rightY += 5;
  }
  if (includeIban && contractor.iban) {
    doc.text(`${l.ibanLabel}: ${contractor.iban}`, rightX, rightY, { align: 'right' });
  }
}

export function renderItemsTable(
  doc: jsPDF,
  items: StructuredData['items'],
  footRows: string[][],
  startY: number,
  vatRate: number | null,
  markupPercent: number | null,
  labels?: Partial<PdfLabels>,
): void {
  const l = resolveLabels(labels);
  const markupMultiplier = markupPercent ? 1 + markupPercent / 100 : 1;

  if (vatRate === null) {
    // 4-column layout without VAT
    const head = [[l.description, l.quantity, l.unitPrice, l.itemTotal]];
    const body = items.map((item) => {
      const effectivePrice = item.unit_price * markupMultiplier;
      const lineTotal = item.quantity * effectivePrice;
      return [
        item.description,
        item.quantity.toString(),
        `€${effectivePrice.toFixed(2)}`,
        `€${lineTotal.toFixed(2)}`,
      ];
    });

    autoTable(doc, {
      startY,
      head,
      body,
      foot: footRows,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [34, 87, 60] },
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
      columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
      },
    });
  } else {
    // 5-column layout with VAT per line item
    const head = [[l.description, l.quantity, l.unitPrice, l.vatColumn, l.itemTotal]];
    const body = items.map((item) => {
      const effectivePrice = item.unit_price * markupMultiplier;
      const lineNet = item.quantity * effectivePrice;
      const lineVat = lineNet * (vatRate / 100);
      const lineTotal = lineNet + lineVat;
      return [
        item.description,
        item.quantity.toString(),
        `€${effectivePrice.toFixed(2)}`,
        `€${lineVat.toFixed(2)}`,
        `€${lineTotal.toFixed(2)}`,
      ];
    });

    // Adjust footer rows to 5 columns
    const adjustedFoot = footRows.map((row) => {
      if (row.length === 4) {
        return ['', '', '', row[2], row[3]];
      }
      return row;
    });

    autoTable(doc, {
      startY,
      head,
      body,
      foot: adjustedFoot,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [34, 87, 60] },
      footStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0], fontStyle: 'bold' },
      columnStyles: {
        1: { halign: 'right' },
        2: { halign: 'right' },
        3: { halign: 'right' },
        4: { halign: 'right' },
      },
    });
  }
}

export function renderNotesSection(
  doc: jsPDF,
  notes: string,
  yOffset: number,
  notesHeading?: string,
): void {
  const pageWidth = doc.internal.pageSize.width;
  const finalY = getTableFinalY(doc);
  const splitText = doc.splitTextToSize(notes, pageWidth - 28);
  const notesHeight = 10 + splitText.length * 4;
  const pageHeight = doc.internal.pageSize.height;

  let startY = finalY + yOffset;
  if (startY + notesHeight > pageHeight - 20) {
    doc.addPage();
    startY = 20;
  }

  const heading = notesHeading || 'Notes';
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(heading, 14, startY);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(splitText, 14, startY + 6);
}

export function renderPageFooter(doc: jsPDF, generatedByText = 'Generated by VoiceQuote'): void {
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text(generatedByText, 14, doc.internal.pageSize.height - 10);
  }
}
